import React, { useState, useEffect } from "react";
import {
    Box, Typography, CircularProgress, Divider
} from "@mui/material";
import { useGetSettingByKeyQuery, useUpsertSettingMutation } from "../../../../../reducers/settingsApi";
import ToggleSwitch from "../../../../../components/ios-radio-button/ToggleSwitch";
import { toast } from "react-toastify";

const GlobalPricingSettings = ({ onClose }) => {
    const settingKey = "app.settings.useDymaicPricing";
    const { data: settingRes, isLoading: isFetching } = useGetSettingByKeyQuery(settingKey);
    const [upsertSetting, { isLoading: isUpdating }] = useUpsertSettingMutation();

    const [settings, setSettings] = useState({
        invalid: "no",
        notFound: "no"
    });

    useEffect(() => {
        if (settingRes?.data?.value) {
            let value = settingRes.data.value;
            if (typeof value === "string") {
                try {
                    value = JSON.parse(value);
                } catch (e) {
                    console.error("Failed to parse global pricing settings value", e);
                }
            }
            if (value && typeof value === "object") {
                setSettings({
                    invalid: value.invalid === "yes" ? "yes" : "no",
                    notFound: value.notFound === "yes" ? "yes" : "no"
                });
            }
        }
    }, [settingRes]);

    const handleSave = async () => {
        try {
            const payload = {
                key: settingKey,
                value: settings,
                description: "use dynamic price of invalid or notfund yes or no",
                category: "pricing",
                isPublic: true,
                dataType: "string"
            };

            await upsertSetting(payload).unwrap();
            toast.success("Pricing settings updated successfully");
            if (onClose) onClose();
        } catch (error) {
            console.error("Failed to save pricing settings:", error);
            toast.error(error?.data?.message || "Failed to update pricing settings");
        }
    };

    if (isFetching) {
        return (
            <Box sx={{ display: "flex", justifyContent: "center", p: 4 }}>
                <CircularProgress size={24} />
            </Box>
        );
    }

    return (
        <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 2 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1, color: "#000" }}>
                Global Dynamic Pricing Settings
            </Typography>
            <Typography variant="body2" sx={{ mb: 2, color: "#666" }}>
                Configure whether the dynamic pricing system should apply to items with invalid links or items not found.
            </Typography>

            <Divider />

            <Box sx={{ display: "flex", flexDirection: "column", gap: 3, mt: 2 }}>
                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#212529" }}>
                            Dynamic Pricing for Invalid Links
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6c757d" }}>
                            Use dynamic pricing when link status is marked as invalid.
                        </Typography>
                    </Box>
                    <ToggleSwitch
                        Name="invalid"
                        checked={settings.invalid === "yes"}
                        onChange={(e) => {
                            setSettings(prev => ({
                                ...prev,
                                invalid: e.target.checked ? "yes" : "no"
                            }));
                        }}
                    />
                </Box>

                <Divider />

                <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                    <Box>
                        <Typography variant="subtitle1" sx={{ fontWeight: 600, color: "#212529" }}>
                            Dynamic Pricing for Not Found Links
                        </Typography>
                        <Typography variant="caption" sx={{ color: "#6c757d" }}>
                            Use dynamic pricing when link status is marked as not found.
                        </Typography>
                    </Box>
                    <ToggleSwitch
                        Name="notFound"
                        checked={settings.notFound === "yes"}
                        onChange={(e) => {
                            setSettings(prev => ({
                                ...prev,
                                notFound: e.target.checked ? "yes" : "no"
                            }));
                        }}
                    />
                </Box>
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: "flex", justifyContent: "flex-end", gap: 2, mt: 2 }}>
                <button
                    className="btn btn-light rounded-pill px-4"
                    style={{ border: "1px solid #ced4da" }}
                    onClick={onClose}
                >
                    Cancel
                </button>
                <button
                    className="btn btn-dark rounded-pill px-4"
                    disabled={isUpdating}
                    onClick={handleSave}
                >
                    {isUpdating ? "Saving..." : "Save Settings"}
                </button>
            </Box>
        </Box>
    );
};

export default GlobalPricingSettings;
